import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import './TabsContainer.scss';

export default function TabsContainer({
  tabs,
  defaultActiveTab,
  activeTab: controlledActiveTab,
  onTabChange,
  isVisible = true,
  customStyle = {},
  rightContent,
}) {
  // Find tabValue for defaultActiveTab or fallback to first tab's tabValue
  const getDefaultTabValue = () => {
    if (defaultActiveTab) return defaultActiveTab;
    return tabs.length > 0 ? tabs[0].props.tabValue : '';
  };

  const isControlled = controlledActiveTab !== undefined;
  const [internalActiveTab, setInternalActiveTab] = useState(
    getDefaultTabValue(),
  );

  // Use controlled value if provided, otherwise use internal state
  const activeTab = isControlled ? controlledActiveTab : internalActiveTab;

  useEffect(() => {
    // If activeTab is not found in tabs, reset to first tab
    const tabValues = tabs.map((tab) => tab.props.tabValue);
    if (!tabValues.includes(activeTab)) {
      const firstTabValue = tabValues[0] || '';
      if (isControlled) {
        if (onTabChange) {
          onTabChange(firstTabValue);
        }
      } else {
        setInternalActiveTab(firstTabValue);
      }
    }
  }, [tabs, activeTab, isControlled, onTabChange]);

  const handleTabChange = (tabValue) => {
    if (isControlled) {
      if (onTabChange) {
        onTabChange(tabValue);
      }
    } else {
      setInternalActiveTab(tabValue);
    }
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div className="tabs-container" style={customStyle}>
      <div className="tabs-header">
        <div className="tabs-left">
          {tabs.map((tab) => (
            <div
              key={`tab-${tab.props.tabValue}`}
              className={classNames('tab-item', {
                active: activeTab === tab.props.tabValue,
              })}
              onClick={() => handleTabChange(tab.props.tabValue)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleTabChange(tab.props.tabValue);
                }
              }}
            >
              {tab.props.tabName}
            </div>
          ))}
        </div>
        {rightContent && <div className="tabs-right">{rightContent}</div>}
      </div>
      <div className="tabs-content">
        {tabs.map((tab) => (
          <div
            key={`content-${tab.props.tabValue}`}
            className={classNames('tab-panel', {
              active: activeTab === tab.props.tabValue,
            })}
          >
            {tab}
          </div>
        ))}
      </div>
    </div>
  );
}

export function TabContent({ children }) {
  return <div className="tab-content">{children}</div>;
}

TabsContainer.propTypes = {
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      props: PropTypes.shape({
        tabName: PropTypes.string.isRequired,
        tabValue: PropTypes.string.isRequired,
        children: PropTypes.node,
      }),
    }),
  ).isRequired,
  defaultActiveTab: PropTypes.string,
  activeTab: PropTypes.string,
  onTabChange: PropTypes.func,
  isVisible: PropTypes.bool,
  customStyle: PropTypes.object,
  rightContent: PropTypes.node,
};

TabContent.propTypes = {
  children: PropTypes.node,
};
